import { ui } from '@libs/domain';
import { useItemNames } from '@ps-smart-cp/ppfw-client';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import type { SsoStatus } from '../../../types';
import type { LoginCredentials } from '../models';
import { FormSection } from './FormSection';
import { LoginInputField } from './LoginInputField';
import { LoginLabelField } from './LoginLabelField';
import { StyledLoginButton } from './StyledLoginButton';
import { StyledLoginLink } from './StyledLoginLink';

interface LoginFormProps {
  credentials: LoginCredentials;
  setCredentials: (credentials: LoginCredentials) => void;
  organizationName: string;
  ssoStatus: SsoStatus | null;
  onLogin: () => void;
}

export function LoginForm({
  credentials,
  setCredentials,
  organizationName,
  ssoStatus,
  onLogin,
}: LoginFormProps) {
  const navigate = useNavigate();

  const labelNames = useItemNames(ui.labelNames);
  const buttonNames = useItemNames(ui.buttonNames);
  const linkNames = useItemNames(ui.linkNames);

  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    setCredentials({ ...credentials, [e.currentTarget.name]: e.currentTarget.value });

  return (
    <>
      <FormSection>
        <LoginLabelField
          id="organization-id"
          head={`${labelNames('organizationId')}:`}
          body={credentials.organizationId}
        />
        <LoginInputField
          id="user-id"
          name="username"
          label={`${labelNames('userId')}*`}
          value={credentials.username}
          onChange={onChange}
          onEnter={onLogin}
        />
        <LoginInputField
          id="password"
          name="password"
          label={`${labelNames('password')}*`}
          value={credentials.password}
          onChange={onChange}
          onEnter={onLogin}
          type="password"
        />
      </FormSection>
      <FormSection>
        <StyledLoginButton id="login-btn" displayName={buttonNames('login')} onClick={onLogin} />
        <StyledLoginButton
          id="cancel-btn"
          displayName={buttonNames('cancel')}
          onClick={() => navigate('/')}
        />
      </FormSection>
      <FormSection>
        <StyledLoginLink
          id="sandbox-login"
          to="/sandbox-login"
          displayName={linkNames('sandboxLoginHere')}
          state={{ organizationId: credentials.organizationId, organizationName, ssoStatus }}
        />
      </FormSection>
    </>
  );
}
